import { useMemo } from 'react';
import { Select, Space, Typography } from 'antd';
import ReferencePicker from './ReferencePicker';
import { acqListReferenceTypes } from './referenceTypes';

interface SubcontoPickerProps {
  /**
   * Значення зберігається як JSON-рядок `{"type":"Counterparty","id":"<guid>"}`.
   * Допустимі також: `null`, порожній рядок, або просто GUID (legacy — тип невідомий).
   */
  acqValue?: string | null;
  acqOnChange?: (acqJson: string | null) => void;
  acqReadOnly?: boolean;
  acqDisabled?: boolean;
  acqSize?: 'small' | 'middle' | 'large';
}

interface SubcontoValue {
  type: string | null;
  id: string | null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function acqParse(acqRaw: string | null | undefined): SubcontoValue {
  if (!acqRaw) return { type: null, id: null };
  // Legacy — лише GUID без типу
  if (UUID_RE.test(acqRaw)) return { type: null, id: acqRaw };
  try {
    const acqObj = JSON.parse(acqRaw);
    if (acqObj && typeof acqObj === 'object') {
      return {
        type: typeof acqObj.type === 'string' ? acqObj.type : null,
        id: typeof acqObj.id === 'string' ? acqObj.id : null,
      };
    }
  } catch {
    // не JSON
  }
  return { type: null, id: null };
}

function acqSerialize(acqType: string | null, acqId: string | null): string | null {
  if (!acqType && !acqId) return null;
  return JSON.stringify({ type: acqType, id: acqId });
}

/**
 * Picker для polymorphic Subconto-полів (BAS Characteristic.* — Контрагент/Договір/тощо).
 * UI: два Select-и поряд — тип субконто + значення (через ReferencePicker за обраним типом).
 *
 * Передбачає, що value — JSON-рядок `{ type, id }`. Legacy GUID без типу
 * читається у read-mode як "тип невідомий" + GUID.
 */
export default function SubcontoPicker({
  acqValue, acqOnChange, acqReadOnly = false, acqDisabled, acqSize = 'middle',
}: SubcontoPickerProps) {
  const acqParsed = useMemo(() => acqParse(acqValue), [acqValue]);

  const acqTypeOptions = useMemo(() => acqListReferenceTypes(), []);

  if (acqReadOnly) {
    if (!acqParsed.type && !acqParsed.id) return <Typography.Text type="secondary">—</Typography.Text>;
    if (!acqParsed.type) {
      return <Typography.Text code title="Тип субконто невідомий">{acqParsed.id?.slice(0, 8)}…</Typography.Text>;
    }
    return (
      <Space size={4}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {acqTypeOptions.find((t) => t.value === acqRefTypeKey(acqParsed.type))?.label ?? acqParsed.type}:
        </Typography.Text>
        <ReferencePicker
          acqReferenceType={acqRefTypeKey(acqParsed.type) ?? 'counterparty'}
          acqValue={acqParsed.id ?? undefined}
          acqReadOnly
        />
      </Space>
    );
  }

  const acqHandleType = (acqNewType: string | undefined) => {
    if (!acqOnChange) return;
    // Зміна типу — скидаємо id (бо колишнє значення з іншого довідника)
    acqOnChange(acqSerialize(acqEntityFromRefType(acqNewType ?? null), null));
  };

  const acqHandleId = (acqNewId: string | null) => {
    if (!acqOnChange) return;
    const acqEntity = acqParsed.type ?? acqEntityFromRefType(null);
    acqOnChange(acqSerialize(acqEntity, acqNewId));
  };

  const acqRefKey = acqRefTypeKey(acqParsed.type);

  return (
    <Space.Compact style={{ width: '100%' }}>
      <Select
        showSearch
        placeholder="Тип субконто…"
        size={acqSize}
        disabled={acqDisabled}
        value={acqRefKey ?? undefined}
        onChange={acqHandleType}
        options={acqTypeOptions}
        filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        style={{ width: 220, flex: '0 0 auto' }}
      />
      {acqRefKey ? (
        <div style={{ flex: '1 1 auto' }}>
          <ReferencePicker
            acqReferenceType={acqRefKey}
            acqValue={acqParsed.id ?? undefined}
            acqOnChange={(id) => acqHandleId(id)}
            acqDisabled={acqDisabled}
            acqSize={acqSize}
            acqAllowClear
          />
        </div>
      ) : null}
    </Space.Compact>
  );
}

// Маппінг між `acqEntityNameToReferenceType` (BAS-entity → camelCase key)
// — тримаємо локально щоб не спричиняти круговий імпорт.
const ACQ_ENTITY_TO_KEY: Record<string, string> = {
  Counterparty: 'counterparty',
  CounterpartyContract: 'contract',
  Nomenclature: 'nomenclature',
  NomenclatureGroup: 'nomenclatureGroup',
  AccountingWarehouse: 'warehouse',
  CostItem: 'costItem',
  BankAccount: 'bankAccount',
  CashFlowItem: 'cashFlowItem', // окремо не у REFERENCE_TYPES — fallthrough на BasRefPicker не реалізовано
  TaxDeclarationItem: 'taxDeclarationItem',
  Tax: 'tax',
  TaxActivityType: 'taxActivityType',
  TaxPurpose: 'taxPurpose',
  FixedAsset: 'fixedAsset',
  IntangibleAsset: 'intangibleAsset',
  Individual: 'individual',
  Employee: 'employee',
  OrganizationDepartment: 'organizationDepartment',
  Document: 'document',
};

const KEY_TO_ENTITY: Record<string, string> = Object.fromEntries(
  Object.entries(ACQ_ENTITY_TO_KEY).map(([acqEntity, acqKey]) => [acqKey, acqEntity]),
);

function acqRefTypeKey(acqEntityName: string | null): string | null {
  if (!acqEntityName) return null;
  return ACQ_ENTITY_TO_KEY[acqEntityName] ?? null;
}

function acqEntityFromRefType(acqKey: string | null): string | null {
  if (!acqKey) return null;
  return KEY_TO_ENTITY[acqKey] ?? null;
}
